/**
 * Ellipse drawer
 */
import Mat3 from "ui/helpers/Mat3";
import Vec2 from "ui/helpers/Vec2";

export default class CanvasEllipse {
	/**
	 * Class constructor
	 *
	 * @param {CanvasRenderingContext2D} ctx
	 * @param {Color} color
	 * @param {T_Point} start
	 * @param {number} [brushSize]
	 */
	constructor(ctx, color, start, brushSize = 1, angle = 0) {
		/**
		 * @type {{initX: number, initY: number, x: number, y: number, width: number, height: number, brushSize: number, angle: number}}
		 * @private
		 */
		this._props = {
			initX: start.x,
			initY: start.y,
			x: 0,
			y: 0,
			width: 0,
			height: 0,
			angle,
			brushSize
		};

		/**
		 * @type {T_Rectangle}
		 * @private
		 */
		this._lastRect = {
			top: start.y - brushSize,
			left: start.x - brushSize,
			right: start.x + brushSize,
			bottom: start.y + brushSize
		};

		/**
		 * @type {CanvasRenderingContext2D}
		 * @private
		 */
		this._context = ctx;

		ctx.lineCap = 'round';
		ctx.lineJoin = 'round';
		ctx.strokeStyle = color.rgbCss;
		ctx.lineWidth = brushSize;
	}

	/**
	 * @param {number} angle
	 */
	setAngle(angle) {
		this._props.angle = angle;
	}

	/**
	 * Returns changed area bounding rectangle
	 *
	 * @returns {T_Rectangle}
	 */
	get lastDrawnRect() {
		return this._lastRect;
	}

	/**
	 * Draws ellipse
	 *
	 * @param {T_Point} pos
	 * @param {boolean} [isCircle]
	 * @returns {CanvasEllipse}
	 */
	draw(pos, isCircle = false) {
		let ctx = this._context;
		let props = this._props;
		props.x = props.initX;
		props.y = props.initY;
		props.width = pos.x - props.x;
		props.height = pos.y - props.y;

		ctx.save();

		let step = Math.PI / 180,
			xRadius = props.width,
			yRadius = props.height;

		if (isCircle) {
			xRadius = yRadius = Math.max(xRadius, yRadius);
		}

		let centerX = props.x,
			centerY = props.y;

		const mat = new Mat3();
		const topLeft = new Vec2(
			-props.brushSize - xRadius,
			-props.brushSize - yRadius
		);
		const topRight = new Vec2(
			xRadius + props.brushSize,
			-props.brushSize - yRadius
		);
		const bottomRight = new Vec2(
			xRadius + props.brushSize,
			yRadius + props.brushSize
		);
		const bottomLeft = new Vec2(
			-props.brushSize - xRadius,
			yRadius + props.brushSize
		);

		if (this._props.angle > 0) {
			mat.rotate(this._props.angle);
		}

		mat.translate(centerX, centerY);

		topLeft.transform(mat);
		topRight.transform(mat);
		bottomRight.transform(mat);
		bottomLeft.transform(mat);

		let lastRect = this._lastRect;
		lastRect.left = Math.min(lastRect.left, topLeft.x, topRight.x, bottomRight.x, bottomLeft.x);
		lastRect.top = Math.min(lastRect.top, topLeft.y, topRight.y, bottomRight.y, bottomLeft.y);
		lastRect.right = Math.max(lastRect.right, topLeft.x, topRight.x, bottomRight.x, bottomLeft.x);
		lastRect.bottom = Math.max(lastRect.bottom, topLeft.y, topRight.y, bottomRight.y, bottomLeft.y);

		const p = new Vec2(0, yRadius);
		p.transform(mat);
		ctx.beginPath();
		ctx.moveTo(p.x, p.y);

		for (let a = 0; a < Math.PI * 2; a += step) {
			const p = new Vec2(
				Math.sin(a) * xRadius,
				Math.cos(a) * yRadius
			);
			p.transform(mat);
			ctx.lineTo(p.x, p.y);
		}

		ctx.stroke();
		ctx.restore();
		return this;
	}
}
